import { Entry } from 'contentful';

import {
  TypeBlogTagSkeleton,
  TypeCountrySkeleton,
  TypeSpeciesSkeleton,
  TypeUsRegionSkeleton,
} from '../../../interfaces/contentful';
import { InternalLink } from '../../Clickables/InternalLink/InternalLink';
import { Pill } from '../../Clickables/Pill/Pill';
import { Heading } from '../../Heading/Heading';
import { Text } from '../../Text/Text';

import { NextImageWrapper } from '../../NextImage/NextImageWrapper';
import { extractSpeciesList } from './helpers/extractSpeciesList';
import { getOrderedSpeciesList } from './helpers/getOrderedSpeciesList';
import { NextImagePlaceholder } from '../../NextImage/NextImagePlaceholder';
import { twMerge } from 'tailwind-merge';
import { HydrationSafeDate } from '../../HydrationSafeDate/HydrationSafeDate';

interface IProps {
  className?: string;
  imageUrl?: string;
  categorySlug: string;
  blogPostId?: string;
  blogPostSlug: string;
  blogPostTitle: string;
  blogPostDescription: string;
  blogTags?: (Entry<TypeBlogTagSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'> | undefined)[];
  species?: (Entry<TypeSpeciesSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'> | undefined)[] | undefined;
  publishDate?: string;
  hideDate?: boolean;
  country?: Entry<TypeCountrySkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'> | undefined;
  speciesId?: string;
  selectedRegion?: Entry<TypeUsRegionSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'> | undefined;
  placeName?: string;
}

export const BlogPreviewCard = ({
  className,
  imageUrl,
  categorySlug,
  blogPostId,
  blogPostSlug,
  blogPostTitle,
  blogPostDescription,
  blogTags,
  species,
  publishDate,
  hideDate = false,
  country,
  speciesId,
  selectedRegion,
  placeName,
}: IProps) => {
  const tagList = blogTags?.map(tag => tag);
  const speciesList = species ? extractSpeciesList(species) : undefined;
  const countryName = country?.fields.countryName ?? undefined;
  const IMAGEWIDTH = 250;
  const IMAGEHEIGHT = 250;
  const orderedSpeciesList = speciesList
    ? getOrderedSpeciesList({ speciesList, speciesId })
    : undefined;

  return (
    <InternalLink
      data-testid="BlogPreviewCard"
      to={`/blog/${categorySlug}/${blogPostSlug}`}
      className={twMerge(
        'bg-lake-fog flex h-max w-full flex-row gap-2 rounded-xl p-3 sm:p-6 md:gap-6',
        className,
      )}
    >
      <div className="flex grow flex-col gap-2 md:gap-3">
        <div className="flex flex-col gap-1">
          <Heading className="mb-0 line-clamp-3 text-ellipsis" as="span" variant="xs">
            {blogPostTitle}
          </Heading>
          {publishDate && !hideDate && (
            <HydrationSafeDate
              className="text-midnight mb-0"
              uniqueLoaderKey={`blogpost-date-${blogPostId ?? blogPostSlug}`}
              date={publishDate}
            />
          )}
        </div>
        <Text variant="sm" className="mb-0 line-clamp-2 font-normal text-ellipsis">
          {blogPostDescription}
        </Text>

        <div className="hidden w-full flex-wrap gap-1 pt-1 sm:flex md:pt-3">
          {countryName && (
            <Pill variant="infoClown" className="cursor-auto" size="sm" disabled>
              {countryName}
            </Pill>
          )}
          {selectedRegion && (
            <Pill variant="infoClown" className="cursor-auto" size="sm" disabled>
              {selectedRegion.fields.regionName}
            </Pill>
          )}
          {placeName && (
            <Pill variant="infoClown" className="cursor-auto" size="sm" disabled>
              {placeName}
            </Pill>
          )}
          {orderedSpeciesList?.map(sp => {
            const { name, externalId } = sp.fields;
            const { id } = sp.sys;
            const variant = speciesId && speciesId === externalId ? 'infoClown' : 'infoBlue';
            return (
              <Pill variant={variant} className="cursor-auto" size="sm" disabled key={id}>
                {name}
              </Pill>
            );
          })}
          {tagList?.map(tag => {
            if (!tag) {
              return <></>;
            }
            const { tagName } = tag.fields;
            const { id } = tag.sys;
            return (
              <Pill as="div" className="cursor-auto" variant="infoBlue" size="sm" disabled key={id}>
                {tagName}
              </Pill>
            );
          })}
        </div>
      </div>
      <div className="flex justify-end">
        {imageUrl ? (
          <NextImageWrapper
            wrapperClassName="w-20 h-20 md:w-44 md:h-44"
            imageClassName="object-cover size-full"
            alt={blogPostTitle}
            src={imageUrl}
            customLoader={{
              customLoaderType: 'contentful',
              origWidth: IMAGEWIDTH,
              origHeight: IMAGEHEIGHT,
            }}
            width={IMAGEWIDTH}
            height={IMAGEHEIGHT}
          />
        ) : (
          <div className="relative h-20 w-20">
            <NextImagePlaceholder />
          </div>
        )}
      </div>
    </InternalLink>
  );
};
